import { Injectable } from '@angular/core'
import {
  AGGREGATE_INFO_GROUP,
  ColumnCatalogEntry,
} from '../models/source-definition.model'
import { WorkbenchRow } from '../models/workbench-row.model'

/**
 * Prometheus dumps unmodeled key/value pairs into an AggregateInformation blob whose keys aren't
 * known ahead of time. We discover the union of keys across the visible rows at runtime and expose
 * each as a dynamic column (hidden by default).
 *
 * The discovered `field` is the blob key VERBATIM — never prefixed or transformed, per the catalog
 * contract that `field` is the only join key. The column's colSource is AGGREGATE_INFO, which is
 * what tells the ColDef factory to read its value out of `row.aggregateInfo[field]` rather than the
 * top-level `fields` map.
 *
 * Discovery predates the catalog returning AGGREGATE_INFO columns of its own. Now that it does,
 * a key present in both would be emitted twice, and the second ColDef would collide on colId —
 * which is how catalogued Aggregate Info columns ended up in an "Other" section. Callers pass the
 * fields the catalog already declares so discovery only fills the genuine gaps.
 */
@Injectable()
export class AggregateInfoService {
  /**
   * @param rows rows whose `aggregateInfo` blobs are scanned, in row order.
   * @param catalogued fields the catalog already describes; skipped so nothing is emitted twice.
   */
  discoverColumns(
    rows: WorkbenchRow[],
    catalogued: ReadonlySet<string> = new Set<string>(),
  ): ColumnCatalogEntry[] {
    const seen = new Set<string>()
    const keys: string[] = []
    rows.forEach(row => {
      if (!row.aggregateInfo) return
      Object.keys(row.aggregateInfo).forEach(key => {
        if (seen.has(key) || catalogued.has(key)) return
        seen.add(key)
        keys.push(key)
      })
    })
    return keys.map(key => ({
      field: key,
      headerName: key.includes('_') ? key.replace(/_/g, ' ') : key,
      cellType: 'text',
      colSource: AGGREGATE_INFO_GROUP,
      // A runtime-discovered blob column IS an agg_info key by definition — that is the only place
      // it could have come from — so it is dynamic without needing the catalog to say so
      // (PVAL-26129 §2). Getting this wrong is silent: routed static, the backend ignores the filter
      // and returns unfiltered rows with no error.
      isDynamic: true,
      // TEXT is a placeholder, not a measurement: the blob is untyped, so the USER owns the
      // comparison type and picks it in the Filter Modal / column header (§4). Membership of the
      // AGGREGATE_INFO group is what makes the picker appear, which is why the group key is stamped
      // here rather than left to the caller.
      valueType: 'TEXT',
      hide: true,
      width: 160,
    }))
  }
}
